import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http'
import { inject } from '@angular/core'
import { defer, switchMap, tap, throwError, timer } from 'rxjs'
import { retry } from 'rxjs/operators'
import { RateLimitService } from '../services/rate-limit.service'

/** Give up after this many 429s on a single request. */
const MAX_RETRIES = 2

/**
 * Backoff floor when the server's reset hint is missing or unreadable (e.g.
 * x-ratelimit-reset not CORS-exposed by an older API deploy). Server windows
 * run up to 60s, so retrying after ~1s just burns the retry budget.
 */
const MIN_BACKOFF_MS = 5000

/**
 * Records the backend's rate-limit budget and protects the API from request
 * storms. Two reactive mechanisms, no proactive "slow down as the limit drops"
 * gradient — the window is ~1s so there is nothing to ration; the only real
 * failure mode is a burst, which is handled here + by bounded concurrency.
 *
 *   1. Hard gate — while the budget is fully spent (remaining === 0), hold the
 *      outgoing request until the window resets, so we don't waste a round-trip
 *      earning a 429.
 *   2. 429 backoff — if we hit the wall anyway, wait the server's exact reset
 *      hint (not a blind guess), escalating exponentially with jitter if it
 *      keeps failing (means other clients are eating the shared budget).
 *
 * Registered LAST so it sits closest to the backend and sees raw responses.
 */
export const rateLimitInterceptor: HttpInterceptorFn = (req, next) => {
  const rl = inject(RateLimitService)

  // defer so the gate is recomputed on every (re)subscribe, including retries.
  return defer(() => {
    const wait = rl.isExhausted() ? rl.msUntilReset() : 0
    return timer(wait).pipe(switchMap(() => next(req)))
  }).pipe(
    tap(event => {
      if (event instanceof HttpResponse) rl.update(event.headers)
    }),
    retry({
      count: MAX_RETRIES,
      delay: (error, attempt) => {
        // Only retry rate-limit rejections; let everything else propagate.
        if (!(error instanceof HttpErrorResponse) || error.status !== 429) {
          return throwError(() => error)
        }
        // The 429 carries fresh headers; trust the server's reset over a guess.
        rl.update(error.headers)
        const base = Math.max(rl.msUntilReset(), MIN_BACKOFF_MS)
        const backoff = base * 2 ** (attempt - 1)
        const jitter = backoff * 0.2 * Math.random() // de-sync retrying tabs
        return timer(backoff + jitter)
      },
    }),
  )
}
